import React, { ReactNode, useState } from 'react';
import { CatalogItemViewModel } from '@common/model/catalog/item/view';
import { PlayButton } from '@cp/ds/src/components/playButton';
import { useIsMobile } from '@cp/ds/src/hooks';
import { Stack } from '@cp/ds/src/components/stack';
import { styled } from '@cp/ds/src/theme/styled';
import { AudioPlayer } from '@cp/ds/src/components/audioPlayer';
import { ResumeCardStatsNew } from 'src/entities/resume/ui/card/statsNew';
import { UserMainData } from 'src/entities/resume/ui/card/userMainData';
import { useCatalogItemMedia } from 'src/entities/resume/model';
import VectorVertical from 'src/assets/shapes/vectors/vectorVertical.svg';
import { ImageSlider } from './components/imageSlider';

export const AUDIO_PLAYER_MODAL_NAME = 'audioPlayer';

type ItemResumeCardProps = {
  achievements?: ReactNode | ReactNode[];
  onlineStatus?: ReactNode;
  data: CatalogItemViewModel;
  favoriteButton?: React.ReactNode;
};

const Root = styled(Stack)(({ theme }) => ({
  overflow: 'hidden',
  position: 'relative',
  backgroundColor: 'white',
  width: '100%',
  maxWidth: '480px',
  [theme.breakpoints.up('md')]: {
    borderRadius: '16px',
    maxWidth: '100%',
  },
}));

const AchievementsWrapper = styled(Stack)({
  justifyContent: 'flex-start',
  gap: '4px',
  position: 'absolute',
  left: '16px',
  top: '16px',
  zIndex: 2,
  padding: '2px',
});

const PlayButtonWrapper = styled(Stack)({
  position: 'absolute',
  left: '50%',
  bottom: '20px',
  transform: 'translateX(-50%)',
  zIndex: 2,
});

const StatsWrapper = styled(Stack)(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.down('md')]: {
    padding: '8px 8px 12px',
    borderTop: `1px solid ${theme.palette['gray middle']}`,
    borderBottom: `1px solid ${theme.palette['gray middle']}`,
  },
}));

const VectorWrapper = styled(Stack)(({ theme }) => ({
  color: theme.palette['gray background'],
  position: 'absolute',
  top: 0,
  right: 0,
  transform: 'scale(-1)',
  width: '65px',
}));

export const ItemResumeCard = ({ achievements, data, onlineStatus, favoriteButton }: ItemResumeCardProps) => {
  const isMobile = useIsMobile();
  const [play, setPlay] = useState(false);
  const { media: catalogItemMedia, icon: CatalogItemMediaIcon } = useCatalogItemMedia(data);

  const handleMediaButtonClick = () => {
    setPlay(!play);
  };

  return (
    <Root direction={{ xs: 'column', md: 'row' }} justifyContent="flex-start">
      <ImageSlider
        alt={[data.clientProfile?.name, data.clientProfile?.surname].filter(Boolean).join(' ')}
        mainPhoto={data?.clientProfile?.mainPhoto}
        photoSet={data?.clientProfile?.photoSet}
      >
        <AchievementsWrapper alignItems="flex-start">{achievements}</AchievementsWrapper>
        {favoriteButton}
        <PlayButtonWrapper>
          {catalogItemMedia && CatalogItemMediaIcon ? (
            play ? (
              <AudioPlayer
                duration={catalogItemMedia.durationInMs}
                onClick={handleMediaButtonClick}
                size="small"
                source={catalogItemMedia.src}
              />
            ) : (
              <PlayButton onClick={handleMediaButtonClick} size={3}>
                <CatalogItemMediaIcon />
              </PlayButton>
            )
          ) : null}
        </PlayButtonWrapper>
      </ImageSlider>
      <StatsWrapper direction={{ xs: 'row', md: 'column' }} justifyContent="space-between">
        {!isMobile && <UserMainData data={data} onlineStatus={onlineStatus} />}
        <ResumeCardStatsNew data={data} fontSize={isMobile ? 'medium' : 'large'} />
      </StatsWrapper>
      {!isMobile && (
        <VectorWrapper>
          <VectorVertical />
        </VectorWrapper>
      )}
    </Root>
  );
};
